<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
    <!-- Overlay sombre -->
    <div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

    <!-- Contenu du modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-3xl">
        <!-- Bouton de fermeture (croix) -->
        <button
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
          aria-label="Fermer le modal"
          tabindex="0"
          @click="close"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div class="px-6 py-5">
          <h3 class="text-lg font-bold text-gray-900 mb-4">
            Importer des {{ entityLabel }}
          </h3>

          <!-- Étape 1 : Sélection fichier -->
          <div v-if="step === 1">
            <!-- Zone de sélection de fichier -->
            <div
              class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
              @click="fileInput?.click()"
              @dragover.prevent
              @drop.prevent="handleFileDrop"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".json,.csv"
                class="hidden"
                @change="onFileChange"
              />
              <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p class="mt-2 text-sm text-gray-600">
                <span class="font-medium text-blue-600 hover:text-blue-500">Cliquez pour sélectionner</span>
                ou glissez-déposez un fichier JSON ou CSV
              </p>
              <p class="text-xs text-gray-500 mt-1">Fichiers .json ou .csv uniquement (max 10MB)</p>
            </div>

            <!-- Affichage du fichier sélectionné -->
            <div v-if="selectedFile" class="bg-green-50 p-4 rounded-md mt-4">
              <div class="flex items-start">
                <svg class="h-5 w-5 text-green-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div class="ml-3 flex-1">
                  <p class="text-sm font-medium text-green-800">
                    📄 {{ selectedFile.name }}
                  </p>
                  <p class="text-sm text-green-700">
                    {{ formatFileSize(selectedFile.size) }} • {{ formatFileDate(selectedFile.lastModified) }}
                  </p>
                  <div class="mt-2 text-xs text-green-600">
                    <span class="inline-flex items-center px-2 py-1 rounded-full bg-green-100">
                      ✓ Fichier valide
                    </span>
                  </div>
                </div>
                <button
                  class="ml-3 text-green-600 hover:text-green-800 transition-colors"
                  title="Changer de fichier"
                  @click.stop="clearFile"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Bouton d'analyse -->
            <button
              v-if="selectedFile"
              :disabled="isImporting"
              class="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-4"
              @click="parseFile"
            >
              <span v-if="isImporting" class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyse en cours...
              </span>
              <span v-else class="flex items-center justify-center">
                🔍 Analyser le fichier
              </span>
            </button>

            <!-- Conseils d'utilisation -->
            <div class="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
              <h4 class="font-medium text-blue-900 mb-2">💡 Conseils</h4>
              <ul class="text-sm text-blue-800 space-y-1">
                <li>• Le fichier doit contenir un tableau d'objets JSON avec les champs requis pour {{ entityLabel.toLowerCase() }}.</li>
                <li>• Les champs obligatoires sont : <span class="font-semibold">{{ requiredFields.join(', ') }}</span></li>
                <li>• Les doublons d'UUID ou les erreurs de validation seront signalés dans le rapport d'import.</li>
                <li>• Si le champ <code>user_id</code> est absent, il sera automatiquement complété avec votre identifiant.</li>
                <li v-if="entityLabel.toLowerCase() === 'utilisateurs'">• Les mots de passe manquants seront générés automatiquement (12 caractères aléatoires).</li>
              </ul>
            </div>

            <div v-if="fileError" class="text-sm text-red-600 mt-2">{{ fileError }}</div>
          </div>

          <!-- Étape 2 : Prévisualisation / détection conflits -->
          <div v-if="step === 2">
            <slot name="step2">
              <div class="mb-2 font-medium">Prévisualisation des données à importer :</div>
              <pre class="bg-gray-100 rounded p-2 text-xs max-h-48 overflow-auto">{{ previewData }}</pre>
              <div v-if="conflicts.length" class="mt-2 text-sm text-yellow-700">
                <div>Conflits détectés :</div>
                <ul class="list-disc ml-5">
                  <li v-for="(conflict, i) in conflicts" :key="i">{{ conflict }}</li>
                </ul>
              </div>
            </slot>
          </div>

          <!-- Étape 3 : Résumé et confirmation -->
          <div v-if="step === 3">
            <slot name="step3">
              <div class="mb-2 font-medium">Résumé de l'import :</div>
              <ul class="list-disc ml-5 text-sm">
                <li>Total à importer : {{ parsedAssets.length }}</li>
                <li>Conflits détectés : {{ conflicts.length }}</li>
              </ul>
              <div v-if="conflicts.length" class="mt-2 text-sm text-yellow-700">
                Certains éléments ne seront pas importés.
              </div>
            </slot>
          </div>

          <!-- Étape 4 : Rapport d'import -->
          <div v-if="step === 4">
            <slot name="step4">
              <div class="mb-2 font-medium">Rapport d'import :</div>
              <pre class="bg-gray-100 rounded p-2 text-xs max-h-48 overflow-auto">{{ importReport }}</pre>
            </slot>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2">
          <button v-if="step === 1" class="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded" @click="close">Annuler</button>
          <button v-if="step === 1 && selectedFile" class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded" @click="parseFile">Suivant</button>
          <button v-if="step === 2" class="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded" @click="step = 1">Retour</button>
          <button v-if="step === 2" class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded" @click="step = 3">Confirmer</button>
          <button v-if="step === 3" class="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded" @click="step = 2">Retour</button>
          <button v-if="step === 3" class="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded" @click="importAssets">Importer</button>
          <button v-if="step === 4" class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded" @click="close">Terminer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { importFromJSON, validateAssetData } from '@/utils/admin';
import { getLocalizedErrorMessage } from '@/utils/errorHandling';
import { ref } from 'vue';

// Types génériques
interface ImportModalProps<T> {
  modelValue: boolean;
  entityLabel: string;
  requiredFields: string[];
  apiCreate: (data: T) => Promise<unknown>;
  existingAssets?: Record<string, unknown>[];
}

const props = withDefaults(defineProps<ImportModalProps<Record<string, unknown>>>(), {
  existingAssets: () => []
});
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'import-success', report: unknown): void;
}>();

const step = ref(1);
const selectedFile = ref<File|null>(null);
const fileError = ref('');
const previewData = ref('');
const parsedAssets = ref<Record<string, unknown>[]>([]);
const conflicts = ref<string[]>([]);
const importReport = ref('');
const isImporting = ref(false);

const authStore = useAuthStore();

function close(): void {
  emit('update:modelValue', false);
  setTimeout(() => {
    step.value = 1;
    selectedFile.value = null;
    fileError.value = '';
    previewData.value = '';
    parsedAssets.value = [];
    conflicts.value = [];
    importReport.value = '';
    isImporting.value = false;
  }, 300);
}

function onFileChange(e: Event): void {
  const files = (e.target as HTMLInputElement).files;
  if (!files || !files[0]) return;
  selectedFile.value = files[0];
  fileError.value = '';
}

async function parseFile(): Promise<void> {
  if (!selectedFile.value) return;
  try {
    const data = await importFromJSON<Record<string, unknown>>(selectedFile.value);
    if (!Array.isArray(data)) throw new Error('Le fichier doit contenir un tableau d’objets.');
    // Détection des doublons
    const existingUuids = new Set(props.existingAssets.map(a => a.uuid));
    const existingTitles = new Set(props.existingAssets.map(a => (a.title || a.appellation || '').toString().toLowerCase()));
    const filtered: Record<string, unknown>[] = [];
    const conflictList: string[] = [];
    data.forEach((item, idx) => {
      const uuid = item.uuid;
      const title = (item.title || item.appellation || '').toString().toLowerCase();
      let hasConflict = false;
      if (uuid && existingUuids.has(uuid)) {
        conflictList.push(`Ligne ${idx + 1} : UUID déjà existant (${uuid})`);
        hasConflict = true;
      }
      if (title && existingTitles.has(title)) {
        conflictList.push(`Ligne ${idx + 1} : Titre déjà existant (${title})`);
        hasConflict = true;
      }
      // Validation simple : champs requis
      props.requiredFields.forEach(f => {
        if (!item[f] || (typeof item[f] === 'string' && !(item[f] as string).trim())) {
          conflictList.push(`Ligne ${idx + 1} : champ requis manquant "${f}"`);
          hasConflict = true;
        }
      });
      if (!hasConflict) filtered.push(item);
    });
    parsedAssets.value = filtered;
    previewData.value = JSON.stringify(filtered.slice(0, 5), null, 2) + (filtered.length > 5 ? '\n... (total ' + filtered.length + ' éléments)' : '');
    conflicts.value = conflictList;
    step.value = 2;
  } catch (err: unknown) {
    fileError.value = 'Erreur de parsing : ' + getLocalizedErrorMessage(err);
  }
}

async function importAssets(): Promise<void> {
  isImporting.value = true;
  const results: { success: boolean; index: number; error?: string; uuid?: string }[] = [];
  const adminUserId = authStore.user?.uuid;
  for (let i = 0; i < parsedAssets.value.length; i++) {
    const originalAsset = { ...parsedAssets.value[i] };
    // Correction spécifique MaterialEntity : mapping provenance_location -> provenance
    if ('provenance_location' in originalAsset && originalAsset.provenance_location) {
      if (typeof originalAsset.provenance_location === 'object' && 'uuid' in (originalAsset.provenance_location as Record<string, unknown>)) {
        originalAsset.provenance = (originalAsset.provenance_location as Record<string, unknown>).uuid;
      } else if (typeof originalAsset.provenance_location === 'string') {
        originalAsset.provenance = originalAsset.provenance_location;
      }
      delete originalAsset.provenance_location;
    }
    // Injection du user_id si absent ou vide AVANT validation
    if (!originalAsset.user_id || typeof originalAsset.user_id !== 'string' || !originalAsset.user_id.trim()) {
      originalAsset.user_id = adminUserId;
    }
    // Validation après injection
    const errors = validateAssetData(originalAsset, props.requiredFields);
    if (errors.length > 0) {
      results.push({ success: false, index: i, error: errors.join(', ') });
      continue;
    }
    try {
      console.log(`[IMPORT] Ligne ${i + 1} - Avant appel API:`, originalAsset);
      const response = await props.apiCreate(originalAsset);
      console.log(`[IMPORT] Ligne ${i + 1} - Succès API:`, response);
      results.push({ success: true, index: i, uuid: (response as Record<string, unknown>)?.uuid as string });
    } catch (error: unknown) {
      console.log(`[IMPORT] Ligne ${i + 1} - Erreur API:`, error);
      let errorMessage = getLocalizedErrorMessage(error);
      
      // Amélioration des messages d'erreur pour les utilisateurs
      if (errorMessage.includes('Erreur serveur')) {
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'detail' in error) {
          errorMessage = (error as { detail: string }).detail;
        }
      }
      
      results.push({ success: false, index: i, error: errorMessage });
    }
  }
  isImporting.value = false;
  // Générer un rapport lisible (string) pour l'affichage
  const successCount = results.filter(r => r.success).length;
  const failCount = results.length - successCount;
  importReport.value =
    `Import terminé.\nSuccès : ${successCount}\nÉchecs : ${failCount}` +
    (failCount > 0 ? '\nDétails :\n' + results.filter(r => !r.success).map(r => `Ligne ${r.index + 1} : ${r.error}`).join('\n') : '');
  emit('import-success', { success: successCount, failed: failCount, details: results });
  step.value = 4;
}

const fileInput = ref<HTMLInputElement | null>(null);

function handleFileDrop(e: DragEvent): void {
  e.preventDefault();
  if (!e.dataTransfer || !e.dataTransfer.files.length) return;
  selectedFile.value = e.dataTransfer.files[0];
  fileError.value = '';
}

function clearFile(): void {
  if (fileInput.value) fileInput.value.value = '';
  selectedFile.value = null;
  fileError.value = '';
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatFileDate(timestamp: number): string {
  return new Date(timestamp).toLocaleString('fr-FR');
}
</script> 